import {
  Button,
  CloseButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconButton,
  IconTrashLine,
  InPlaceEdit,
  List,
  Modal,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { CourseGroupTypeService } from "../../services/CourseGroupTypeService";
import {
  ERROR,
  useNotification,
  WARNING,
} from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo/DialogYesNo";
import { CourseGroupsContext } from "../OrganizationalStructure/OrganizationalStructure";

const ManageGroupTypesModal = ({
  open,
  setOpen,
  groupTypes,
  setGroupTypes,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const courseGroups = useContext(CourseGroupsContext);
  const [updatedGroupTypes, setUpdatedGroupTypes] = useState(null);
  const [newGroupType, setNewGroupType] = useState("");
  const [addedTypes, setAddedTypes] = useState([]);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (groupTypes) {
      setUpdatedGroupTypes(groupTypes);
    }
  }, [groupTypes]);

  const pushAddedType = (type) => {
    const tempAddedTypes = [...addedTypes];
    tempAddedTypes.push({ name: type });
    setAddedTypes(tempAddedTypes);
    setNewGroupType("");
    const tempGroupTypes = [...updatedGroupTypes];
    tempGroupTypes.push({ id: type, label: type, mode: "view" });
    setUpdatedGroupTypes(tempGroupTypes);
  };

  const handleClose = () => {
    setOpen(false);
    setSaving(false);
    setAddedTypes([]);
    setNewGroupType("");
  };

  const handleCreateGroupTypes = async (newGroupTypes) => {
    if (addedTypes.length > 0) {
      let newTypes = await CourseGroupTypeService.createGroupTypes(addedTypes);
      newTypes = newTypes.map((type) => ({
        id: type.id,
        label: type.name,
        mode: "view",
      }));
      return [...newGroupTypes, ...newTypes];
    } else {
      return newGroupTypes;
    }
  };

  const handleUpdateGroupTypes = async (newGroupTypes) => {
    for (const currentType of updatedGroupTypes) {
      const originalType = groupTypes.find(
        (type) => type.id === currentType.id
      );
      if (originalType && originalType.label !== currentType.label) {
        await CourseGroupTypeService.updateGroupType({
          id: currentType.id,
          name: currentType.label,
        });
        newGroupTypes = newGroupTypes.map((type) =>
          type.id === currentType.id
            ? { ...type, label: currentType.label }
            : type
        );
      }
    }
    return newGroupTypes;
  };

  const handleDeleteGroupTypes = async (newGroupTypes) => {
    for (const originalType of groupTypes) {
      const type = updatedGroupTypes.find(
        (type) => type.id === originalType.id
      );
      if (!type) {
        await CourseGroupTypeService.deleteGroupTypeById(originalType.id);
        newGroupTypes = newGroupTypes.filter(
          (type) => type.id !== originalType.id
        );
      }
    }
    return newGroupTypes;
  };

  const handleSubmit = async () => {
    setSaving(true);
    let newGroupTypes = [...groupTypes];
    try {
      newGroupTypes = await handleCreateGroupTypes(newGroupTypes);
      newGroupTypes = await handleUpdateGroupTypes(newGroupTypes);
      newGroupTypes = await handleDeleteGroupTypes(newGroupTypes);
      showNotification(
        t("notifications.general.updateSuccess", { obj: "Course Group Types" })
      );
    } catch {
      showNotification(
        t("notifications.general.updateError", { obj: "Course Group Types" }),
        ERROR
      );
    }
    setGroupTypes(newGroupTypes);
    handleClose();
  };

  const renderEdit = (index, { onBlur, editorRef }) => (
    <Text
      color="primary"
      size="medium"
      as="input"
      type="text"
      defaultValue={updatedGroupTypes[index].label}
      aria-label={t("programs.groupType")}
      onBlur={(e) => {
        onBlur();
        handleChange(index, e);
      }}
      elementRef={editorRef}
    />
  );

  const handleChange = (index, event) => {
    const addedTypesIndex = addedTypes.findIndex(
      (type) => type.name === updatedGroupTypes[index].label
    );
    if (addedTypesIndex !== -1) {
      setAddedTypes([
        ...addedTypes.slice(0, addedTypesIndex),
        { ...addedTypes[addedTypesIndex], name: event.target.value },
        ...addedTypes.slice(addedTypesIndex + 1),
      ]);
    }

    setUpdatedGroupTypes([
      ...updatedGroupTypes.slice(0, index),
      { ...updatedGroupTypes[index], label: event.target.value, mode: "view" },
      ...updatedGroupTypes.slice(index + 1),
    ]);
  };

  const renderEditButton = (props, message) => {
    props.label = message;
    props.isVisible = true;
    return InPlaceEdit.renderDefaultEditButton(props);
  };

  const handleChangeMode = (index, mode) => {
    setUpdatedGroupTypes([
      ...updatedGroupTypes.slice(0, index),
      { ...updatedGroupTypes[index], mode: mode },
      ...updatedGroupTypes.slice(index + 1),
    ]);
  };

  const showRemoveSubjectConfirmation = (type) => {
    const groups = courseGroups.filter((group) => group.groupType === type.id);
    if (groups.length > 0) {
      const groupList = [];
      groups.forEach((group) => {
        groupList.push(group.name);
      });
      const string = `${t("programs.groupTypeInUse")} ${groupList}. ${t(
        "programs.pleaseRemove"
      )}`;
      showNotification(string, WARNING);
    } else {
      setTypeToDelete(type);
      setOpenDeleteConfirmation(true);
    }
  };

  const handleYesConfirmation = () => {
    let tempGroupTypes = [...updatedGroupTypes];
    tempGroupTypes = tempGroupTypes.filter(
      (type) => type.id !== typeToDelete.id
    );
    setUpdatedGroupTypes(tempGroupTypes);

    let tempAddedTypes = [...addedTypes];
    tempAddedTypes = tempAddedTypes.filter(
      (type) => type.name !== typeToDelete.label
    );
    setAddedTypes(tempAddedTypes);

    handleCloseConfirmation();
  };

  const handleCloseConfirmation = () => {
    setOpenDeleteConfirmation(false);
    setTypeToDelete({});
  };

  return (
    <>
      <Modal open={open} size="medium" label={t("programs.manageGroupTypes")}>
        <Modal.Header>
          <Grid hAlign="space-between">
            <Grid.Row>
              <Grid.Col>
                <Heading level="h2" as="h1">
                  {t("programs.manageGroupTypes")}
                </Heading>
              </Grid.Col>
              <Grid.Col width="auto">
                <CloseButton
                  size="small"
                  screenReaderLabel={t("global.close")}
                  onClick={handleClose}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </Modal.Header>
        <Modal.Body>
          <Flex alignItems="end" margin="0 0 small 0">
            <Flex.Item shouldGrow>
              <TextInput
                value={newGroupType}
                id="add-group-type"
                renderLabel={
                  <Text size="small">{t("programs.addGroupType")}</Text>
                }
                onChange={(event) => setNewGroupType(event.target.value)}
              />
            </Flex.Item>
            <Flex.Item margin="0 0 0 small">
              <Button
                color="primary"
                renderIcon={IconAddLine}
                onClick={() => pushAddedType(newGroupType)}
                interaction={newGroupType === "" ? "disabled" : "enabled"}
              >
                {t("global.add")}
              </Button>
            </Flex.Item>
          </Flex>
          <List isUnstyled>
            {updatedGroupTypes &&
              updatedGroupTypes.map((type, index) => (
                <List.Item margin="0 0 small 0" key={index}>
                  <Flex justifyItems="space-between">
                    <Flex.Item shouldGrow>
                      <InPlaceEdit
                        renderViewer={() => <Text>{type.label}</Text>}
                        renderEditor={(props) => renderEdit(index, props)}
                        renderEditButton={(props) =>
                          renderEditButton(
                            props,
                            t("programs.editCourseGroupType")
                          )
                        }
                        onChangeMode={(mode) => handleChangeMode(index, mode)}
                        mode={type.mode}
                        value={type.label}
                        inline={false}
                      />
                    </Flex.Item>
                    <Flex.Item>
                      <IconButton
                        size="small"
                        id={`remove-group-type`}
                        renderIcon={IconTrashLine}
                        onClick={() => showRemoveSubjectConfirmation(type)}
                        screenReaderLabel={t("programs.removeGroupType")}
                        margin="0 0 0 small"
                      />
                    </Flex.Item>
                  </Flex>
                </List.Item>
              ))}
          </List>
        </Modal.Body>
        <Modal.Footer>
          <Button margin="0 small" onClick={handleClose}>
            {t("global.cancel")}
          </Button>
          <Button color="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              t("global.save")
            )}
          </Button>
        </Modal.Footer>
      </Modal>
      <DialogYesNo
        message={t("programs.areYouSureYouWantToDelete")}
        openStatus={openDeleteConfirmation}
        onClickYes={handleYesConfirmation}
        onClickNo={handleCloseConfirmation}
      />
    </>
  );
};

export default ManageGroupTypesModal;
